import { ActionStatus } from 'thunkless';
import produce from 'immer';

import { WhatsappTypes } from '../actions/whatsapp';

export type WhatsappSenderStatus =
  | 'pending' | 'creating' | 'pending_verification' | 'verifying' | 'offline' | 'online' | 'failed' | string;

export interface WhatsappSender {
  id: number;
  phone_number: string;
  display_name: string | null;
  waba_id: string | null;
  status: WhatsappSenderStatus;
  twilio_number: boolean;
  verification_method: 'sms' | 'voice' | null;
  offline_reasons: { code?: string; message?: string }[] | null;
  otp_code: string | null;
  otp_from: string | null;
  otp_received_at: string | null;
  last_error: string | null;
  registered_at: string | null;
  created_at: string;
}

export interface WhatsappConfig {
  app_id: string | null;
  config_id: string | null;
  partner_solution_id: string | null;
  waba_id: string | null;
}

export interface WhatsappState {
  fetchStatus: ActionStatus;
  senders: WhatsappSender[];
  phoneNumbers: string[];
  config: WhatsappConfig | null;
  connectStatus: ActionStatus;
  connectError: string | null;
  verifyStatus: Record<number, ActionStatus>;
  verifyError: Record<number, string | null>;
}

export const initialState: WhatsappState = {
  fetchStatus: null,
  senders: [],
  phoneNumbers: [],
  config: null,
  connectStatus: null,
  connectError: null,
  verifyStatus: {},
  verifyError: {},
};

// thunkless wraps the rejection as payload.error; prefer the server's error list, then the client message
const errorMessage = (action: any, fallback: string): string => {
  const error = action.payload?.error;
  const errors = error?.responseJSON?.errors ?? error?.response?.errors;
  if (Array.isArray(errors) && errors.length) return errors.join(', ');
  return error?.message || fallback;
};

const upsertSender = (state: WhatsappState, sender: WhatsappSender) => {
  const index = state.senders.findIndex(({ id }) => id === sender.id);
  if (index === -1) state.senders.unshift(sender);
  else state.senders[index] = sender;
};

export const reducer = produce((state: WhatsappState, action: any) => {
  switch (action.type) {
    case WhatsappTypes.FETCH_SENDERS_REQUEST: {
      state.fetchStatus = ActionStatus.BUSY;
      break;
    }
    case WhatsappTypes.FETCH_SENDERS_SUCCESS: {
      const { data } = action.payload;
      state.fetchStatus = ActionStatus.SUCCESS;
      state.senders = data.senders;
      state.phoneNumbers = data.phone_numbers;
      state.config = data.config;
      break;
    }
    case WhatsappTypes.FETCH_SENDERS_FAILURE: {
      state.fetchStatus = ActionStatus.FAILURE;
      break;
    }

    case WhatsappTypes.CREATE_SENDER_REQUEST:
    case WhatsappTypes.REGISTER_SENDER_REQUEST: {
      state.connectStatus = ActionStatus.BUSY;
      state.connectError = null;
      break;
    }
    case WhatsappTypes.CREATE_SENDER_SUCCESS: {
      upsertSender(state, action.payload.data.sender);
      break;
    }
    case WhatsappTypes.REGISTER_SENDER_SUCCESS: {
      const { sender } = action.payload.data;
      upsertSender(state, sender);
      state.connectStatus = ActionStatus.SUCCESS;
      if (sender.waba_id && state.config && !state.config.waba_id) state.config.waba_id = sender.waba_id;
      break;
    }
    case WhatsappTypes.CREATE_SENDER_FAILURE: {
      state.connectStatus = ActionStatus.FAILURE;
      state.connectError = errorMessage(action, 'Could not start connecting this number.');
      break;
    }
    case WhatsappTypes.REGISTER_SENDER_FAILURE: {
      state.connectStatus = ActionStatus.FAILURE;
      state.connectError = errorMessage(action, 'Could not register this number with WhatsApp.');
      break;
    }
    case WhatsappTypes.SET_CONNECT_ERROR: {
      state.connectError = action.payload.message;
      if (action.payload.message) state.connectStatus = ActionStatus.FAILURE;
      break;
    }

    case WhatsappTypes.FETCH_SENDER_SUCCESS: {
      upsertSender(state, action.payload.data.sender);
      break;
    }

    case WhatsappTypes.VERIFY_SENDER_REQUEST: {
      state.verifyStatus[action.meta.id] = ActionStatus.BUSY;
      state.verifyError[action.meta.id] = null;
      break;
    }
    case WhatsappTypes.VERIFY_SENDER_SUCCESS: {
      upsertSender(state, action.payload.data.sender);
      state.verifyStatus[action.meta.id] = ActionStatus.SUCCESS;
      break;
    }
    case WhatsappTypes.VERIFY_SENDER_FAILURE: {
      state.verifyStatus[action.meta.id] = ActionStatus.FAILURE;
      state.verifyError[action.meta.id] = errorMessage(action, 'Could not submit the verification code.');
      break;
    }

    case WhatsappTypes.DESTROY_SENDER_SUCCESS: {
      state.senders = state.senders.filter(({ id }) => id !== action.meta.id);
      break;
    }
    default:
      break;
  }
}, initialState);

export default reducer;
